import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { IAgoraRTCRemoteUser, ICameraVideoTrack } from 'agora-rtc-react';

import { useAppContext } from '@/app/AppContext';
import VideoDisplay, { VideoTrackInfo } from '@/app/components/VideoDisplay';
import { useMeetingData, useUserData } from '@/app/queries/MeetingQuery/context';

import { cn } from '@/lib/utils';
import { EVENTS, gtag } from '@/lib/gtag';

interface VideoTracksDisplayProps {
  localCameraTrack: ICameraVideoTrack;
  remoteUsers: IAgoraRTCRemoteUser[];
  showVideoTracks: boolean;
}

export default function VideoTracksDisplay({ localCameraTrack, remoteUsers, showVideoTracks }: VideoTracksDisplayProps) {
  const { camera, microphone } = useAppContext();

  const userData = useUserData();
  const { participants } = useMeetingData();

  const knownUids = useMemo(() => new Set(
    participants.flatMap(p => [p.user_id, p.screen_share_id])
  ), [participants]);

  const filteredRemoteUsers = useMemo(
    () => remoteUsers.filter(user => knownUids.has(user.uid as number)),
    [remoteUsers, knownUids]
  );

  const [videoTracks, setVideoTracks] = useState<VideoTrackInfo[]>([]);

  const handleVideoClick = useCallback((clickedVideoTrack: VideoTrackInfo) => {
    gtag(EVENTS.FOCUS_STREAM);

    setVideoTracks(videoTracks => videoTracks.map(video => ({
      ...video,
      isEnlarged: video.source === clickedVideoTrack.source
    })));
  }, []);

  useEffect(function initTracks() {
    setVideoTracks(prevTracks => {
      return [
        {
          uid: userData.uid,
          source: localCameraTrack,
          sourceType: 'local',
          isEnlarged: prevTracks.find(track => track.uid === userData.uid)?.isEnlarged ?? true,
          hasAudio: microphone.isEnabled,
          hasVideo: camera.isEnabled,
        },
        ...filteredRemoteUsers.map(user => {
          const existingTrack = prevTracks.find(track => track.uid === user.uid);

          return {
            uid: user.uid as number,
            source: user,
            sourceType: 'remote' as VideoTrackInfo['sourceType'],
            isEnlarged: existingTrack ? existingTrack.isEnlarged : false,
            hasAudio: user.hasAudio,
            hasVideo: user.hasVideo,
          }
        }),
      ]
    });
  }, [localCameraTrack, filteredRemoteUsers, userData.uid, microphone.isEnabled, camera.isEnabled]);

  const stageTracks = videoTracks.filter(video => video.isEnlarged);
  const pictureInPictureTracks = videoTracks.filter(video => !video.isEnlarged);

  return (
    <div className={cn('absolute inset-0', { 'invisible': !showVideoTracks })}>
      {stageTracks.map(video => (
        <Fragment key={video.uid}>
          <VideoDisplay
            className={cn('MainVideo absolute inset-0', { 'is-disabled': !video.source })}
            onClick={handleVideoClick} variant="stage" videoTrack={video} />
        </Fragment>
      ))}

      {pictureInPictureTracks.length > 0 && (
        <div className="absolute left-4 top-20 z-20 flex flex-col gap-3">
          {pictureInPictureTracks.map(video => (
            <div
              className="relative aspect-portrait w-24 cursor-pointer overflow-hidden rounded-xl shadow-lg ring-1 ring-white/20 md:w-32"
              key={video.uid}
              title="Swap with the main view">
              <VideoDisplay onClick={handleVideoClick} videoTrack={video} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
